import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import pick from 'lodash/pick';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';

import { Loader } from '../../../components/Loader/Loader';
import { CURRENT_PAYMENTS_PROJECT_URL } from '../../../config/constants';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { usePurchaseLimit } from '../../../hooks/usePurchaseLimit';
import { analytics } from '../../../services/analytics';
import { logToNewRelic } from '../../../services/debug';
import { firebaseAuth } from '../../../services/firebase';
import { intercom } from '../../../services/intercom';
import { useFlag } from '../../../services/launchDarkly';
import { pendingTransactionsRegistry } from '../../../services/pendingTransactionsRegistry';
import { platformContextService } from '../../../services/platformContext/PlatformContextService';
import { SubscriptionUtils } from '../../../stores/subscription/utils';
import type { IOpenDialog } from '../../../stores/ui/types';
import { EnumOpenDialog } from '../../../stores/ui/types';
import { useStores } from '../../../stores/useStores';
import { PaymentRouterProviderEnum } from '../../../types';
import styles from '../shared/ExternalPayments.module.scss';
import { assumeForeignHtmlStyle, isPaymentsAppMessage, LAST_STARTED_TRANSACTION } from '../shared/helpers';

import type { PaymentsProps } from './types';
import { isSubscriptionPackage } from './types.utils';

export const ExternalPaymentsV2 = observer(function ExternalPaymentsV2(props: PaymentsProps) {
  const { gameId } = useParams();
  const { bingoStore, loyaltyProgramStore, uiStore, appStore, userStore, payRedeemStore, portalStore } = useStores();
  const [authToken, setAuthToken] = useState<string>();
  const [iframeStyle, setIframeStyle] = useState<{
    height?: number | string;
    background?: string;
  }>();
  const [iframeLoading, setIframeLoading] = useState(true);

  const isForeign = useRef(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const externalPaymentUrl = useFlag<string>('payments_project_url', CURRENT_PAYMENTS_PROJECT_URL);
  const paymentRouter = useFlag<PaymentRouterProviderEnum>('payment_router', PaymentRouterProviderEnum.SUNFLOWER);

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'window-resize', details: { isMobile } }, '*');
  }, [isMobile]);

  useEffect(() => {
    const fetchAuthToken = async () => {
      const token = await firebaseAuth.currentUser?.getIdToken();
      if (token) {
        window.fireBaseToken = token;
        window.fireBaseUid = firebaseAuth.currentUser?.uid;
      } else {
        logToNewRelic('External payments: getFireBaseToken', 'Could not get fire base token', 'debug');
      }
      setAuthToken(token);
    };

    void fetchAuthToken();

    return () => {
      window.fireBaseToken = undefined;
      window.fireBaseUid = undefined;
    };
  }, []);

  const openDialog = useCallback(
    (args: IOpenDialog) => {
      portalStore.closePortal();
      uiStore.setOpenDialog(args);
    },
    [uiStore, portalStore],
  );

  const onFail = useCallback(
    (failureDetails?: string | number | null) =>
      openDialog({
        dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
        options: { type: 'purchase', failureDetails },
      }),
    [openDialog],
  );

  const onPurchaseLimitExceeded = usePurchaseLimit();

  const { currentOrderPackage, selectedPackage } = appStore;
  const activeEvent = currentOrderPackage?.eventTriggerData?.activeEvent;
  const benefit = activeEvent?.benefit ?? {};

  const activeEventFields = pick(activeEvent, [
    'eventTrigger.eventTriggerId',
    'userEventTriggerId',
    'eventTrigger.triggerKey',
    'eventTrigger.conditionsKey',
  ]);
  const benefitFields = pick(benefit, [
    'offerTypeId',
    'key',
    'isActive',
    'offerId',
    'packageId',
    'offerGroupKey',
    'benefitType',
  ]);

  const eventDataWithoutLayouts = activeEvent
    ? {
        activeEvent: {
          ...activeEventFields,
          benefit: {
            ...benefitFields,
          },
        },
      }
    : undefined;

  const eventTriggerData = eventDataWithoutLayouts ? JSON.stringify(eventDataWithoutLayouts) : undefined;

  const iframeParams = useMemo(() => {
    if (props.presetIframeParams && props.errorCode) {
      const presetIframeParams = new URLSearchParams(props.presetIframeParams);
      if (!presetIframeParams.has('errorCode') || presetIframeParams.get('errorCode') !== `${props.errorCode}`) {
        presetIframeParams.set('errorCode', `${props.errorCode}`);

        if (typeof props.errorDetails === 'string') {
          presetIframeParams.set('errorDetails', props.errorDetails);
        }

        return presetIframeParams.toString();
      }

      return props.presetIframeParams;
    }

    if (!selectedPackage?.packageId || !userStore.profile?.firebaseUid || !authToken) {
      return undefined;
    }

    const purchaseSource = sessionStorage.getItem('purchaseSource');
    const params = new URLSearchParams();

    const { platform, paymentEmitterPlatform } = platformContextService.getConfig();

    params.set('packageId', `${selectedPackage.packageId}`);
    params.set('firebaseUid', userStore.profile.firebaseUid);
    params.set('platform', platform);
    if (paymentEmitterPlatform) {
      params.set('paymentEmitterPlatform', paymentEmitterPlatform);
    }
    params.set('version', '3');

    params.set('storeType', props.storeType);
    params.set('storeSegment', loyaltyProgramStore.currentTierKey);
    params.set('amount', String(selectedPackage.price));
    params.set('language', userStore.profile.language);
    params.set('country', userStore.profile.territory.countryCode);
    params.set('currency', userStore.profile.currency.code);

    const isSubscription = isSubscriptionPackage(selectedPackage);
    if (isSubscription) {
      const subscription = selectedPackage.subscriptionData;
      const quantity = subscription.quantity || 1;
      const packageInfo = {
        packageId: subscription.packageId,
        price: subscription.price * quantity,
        ccCards: SubscriptionUtils.getCardsCounts(subscription.benefitPackage.packageBundles, 'GC') * quantity,
        sweepsCards: SubscriptionUtils.getCardsCounts(subscription.benefitPackage.packageBundles, 'CASH') * quantity,
        subscriptionType: subscription.type || 'subscription',
        billingCycle: subscription.cycleType,
        subscriptionName: subscription.name,
        quantity: quantity,
      };
      params.set('package', JSON.stringify(packageInfo));
      params.set('planId', subscription.id);
    } else {
      const packageInfo = {
        packageId: selectedPackage.packageId,
        price: selectedPackage.price,
        gcCoin: selectedPackage.gcAmount,
        scCoin: selectedPackage.scAmount,
      };
      params.set('package', JSON.stringify(packageInfo));
    }

    params.set('fireBaseUid', firebaseAuth.currentUser?.uid ?? '');
    params.set('origin', window.location.origin);

    if (eventTriggerData) {
      params.set('eventTriggerData', eventTriggerData);
    }

    if (typeof props.paymentMethod === 'string') {
      params.set('paymentMethod', props.paymentMethod);
    }

    if (gameId) {
      params.set('gameId', gameId);
    }

    if (props.errorCode) {
      params.set('errorCode', `${props.errorCode}`);
    }

    if (typeof props.errorDetails === 'string') {
      params.set('errorDetails', props.errorDetails);
    }

    if (purchaseSource) {
      params.set('purchaseSource', purchaseSource);
    }

    return params.toString();
  }, [
    authToken,
    eventTriggerData,
    gameId,
    loyaltyProgramStore.currentTierKey,
    props.errorCode,
    props.errorDetails,
    props.paymentMethod,
    props.presetIframeParams,
    props.storeType,
    selectedPackage,
    userStore.profile?.firebaseUid,
    userStore.profile?.language,
    userStore.profile?.territory.countryCode,
    userStore.profile?.currency?.code,
  ]);

  useEffect(() => {
    const iframeMessageListener = (event: MessageEvent<unknown>) => {
      if (!isPaymentsAppMessage(event.data)) {
        return;
      }

      if ('action' in event.data) {
        if (event.data.action === 'close') {
          portalStore.closePortal();
          payRedeemStore.awaitForTransaction();
        }

        return;
      }

      if (!('class' in event.data)) {
        return onFail(event.data.message);
      }

      switch (event.data.class) {
        case 'analytics':
          if (event.data.type === 'event' && event.data.details) {
            void analytics.sendEvent(event.data.details.name, event.data.details.params);
          }
          return;

        case 'fail': {
          switch (event.data.type) {
            case 'kyc-required':
              return openDialog({ dialog: EnumOpenDialog.isOpenKycDialog });
            case 'address-required':
              return openDialog({ dialog: EnumOpenDialog.isOpenTransactionFailedDueToAddressDialog });
            case 'limit-reached':
              return onPurchaseLimitExceeded();
            case 'notice':
              return openDialog({ dialog: EnumOpenDialog.isOpenOrderSummaryDialog });
            case 'general':
              if (event.data.details?.isHandled) {
                isForeign.current = false;
                return;
              }

              return onFail(event.data.details?.message);
            default:
              logToNewRelic(
                'Web + Payments App',
                `Received unknown failure message ${JSON.stringify(event.data)}`,
                'error',
              );

              return onFail();
          }
        }

        case 'flow': {
          switch (event.data.type) {
            case 'size-change':
              if (!isForeign.current) {
                setIframeStyle({ height: event.data.details?.height });
              }

              return;
            case 'loading-finished':
              return setIframeLoading(false);
            case 'exit':
              uiStore.unblockDialog(EnumOpenDialog.isOpenTransactionFailedDialog);
              if (isSubscriptionPackage(selectedPackage)) {
                return bingoStore.openBingoStore();
              }

              return openDialog({
                dialog: EnumOpenDialog.isOpenOrderSummaryDialog,
                options: { isError: true },
              });
            case 'success': {
              const { amount, transactionId, isFirstDeposit } = event.data.details || {
                amount: selectedPackage?.price,
                transactionId: '',
                isFirstDeposit: false,
              };

              logToNewRelic(
                'pre sendSuccessPurchaseEvent',
                {
                  message: 'in ExternalPayments',
                  data: {
                    amount,
                    transactionId,
                    isFirstDeposit,
                  },
                },
                'info',
              );

              if (transactionId) {
                payRedeemStore.setInProcessTransactionId(transactionId);
                payRedeemStore.transactionsEventsRegistry.set(transactionId, {
                  amount: amount ?? selectedPackage?.price ?? 0,
                  isFirstDeposit,
                  transactionId,
                });
              }
              portalStore.closePortal();
              payRedeemStore.handleSuccessPurchase();
              return;
            }
            case 'redirect':
              isForeign.current = true;

              if (event.data.details && iframeParams) {
                const { transactionId, isFirstDeposit, separateVersion } = event.data.details;
                if (transactionId && !separateVersion) {
                  payRedeemStore.inProcessTransactionId = transactionId;
                  sessionStorage.setItem(
                    LAST_STARTED_TRANSACTION,
                    JSON.stringify({
                      transactionId,
                      isFirstDeposit,
                      amount: selectedPackage?.price ?? 0,
                      selectedPackage,
                    }),
                  );
                  pendingTransactionsRegistry.addPendingTransaction(transactionId, iframeParams);
                }
              }

              return setIframeStyle(assumeForeignHtmlStyle(event.data.details?.url));
            case 'ucv':
              return void payRedeemStore.getUcvStatus();
            case 'buttonAction':
              if (event.data.details?.kind === 'support') {
                intercom.handleOpenSupport();
              }
              return;
            case 'failureHandled':
              return uiStore.blockDialog(EnumOpenDialog.isOpenTransactionFailedDialog);
            case 'authenticate':
              return event.source?.postMessage(
                { type: 'authenticate', details: { token: authToken } },
                { targetOrigin: event.origin },
              );
          }
        }
      }
    };

    window.addEventListener('message', iframeMessageListener);

    return () => {
      uiStore.unblockDialog(EnumOpenDialog.isOpenTransactionFailedDialog);
      window.removeEventListener('message', iframeMessageListener);
    };
  }, [
    onFail,
    onPurchaseLimitExceeded,
    payRedeemStore,
    openDialog,
    portalStore,
    iframeParams,
    uiStore,
    selectedPackage,
    authToken,
    bingoStore,
  ]);

  if (!iframeParams) {
    return <Loader />;
  }

  return (
    <>
      <iframe
        ref={iframeRef}
        allow="payment"
        className={styles.iframe}
        data-loading={iframeLoading}
        sandbox="allow-scripts allow-forms allow-same-origin allow-top-navigation"
        src={`${externalPaymentUrl}?paymentRouter=${paymentRouter}&${iframeParams}`}
        style={iframeStyle}
      />
      {iframeLoading && (
        <div className={styles.loaderWrapper}>
          <Loader />
        </div>
      )}
    </>
  );
});
